<template>
  <v-container fluid>
    <PageTitle
      :title="pageTitle"
      :counter_count="activeUsersCount"
      counter_label="Active"
    />
    <div class="mt-3" style="border: 1px solid #e5e5e5; border-radius: 8px">
      <ExBreadcrumbs class="mt-2 ml-1" :breadcrumbsList="dynamicBreadcrumbs" />
      <div class="account-header">
        <img
          class="mr-1"
          :src="require('@components/assets/icons/account_icon.svg')"
          alt="icon"
        />
        <div>
          <AccountStatusBadge :status="selectedAccount.status" />
          <p class="account-header-name">{{ selectedAccount.account }}</p>
        </div>
        <p class="account-header-email">{{ selectedAccount.email }}</p>
      </div>
      <HorizontalMenu
        class="px-3"
        :items="menuItems"
        size="small"
        :hideLines="true"
        :value="selectedTab"
        @onChange="handleTabChange"
      />
      <UniversalTable
        v-if="selectedTab === 0"
        :config="customTableConfig"
        :items="filteredUsers"
        @onActionClick="viewState.state.addPayRangeModal.show = true"
        @onSearch="viewState.state.filters.searchText = $event"
        @onClick="viewState.state.filters.showFilterDialog = true"
        @removeFilter="viewState.removeFilters"
        :emptyObjectData="emptyObjectData"
        :breadcrumItems="dynamicBreadcrumbs"
      >
        <template #Dropdown-activator>
          <div class="icon-container">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="icon-svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </template>
      </UniversalTable>

      <ChatterBlankView v-if="selectedTab === 1" />
    </div>
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />

    <OwnerShipModal
      :dialog="viewState.state.showOnwerShipDialog.show"
      @onAction="transferOwner($event)"
      @back="() => (viewState.state.showOnwerShipDialog.show = false)"
      :items="items"
    />

    <ExActionsPopUp
      v-if="userAccountActionDialogObject?.show ?? false"
      :headline="userAccountActionDialogObject?.title"
      :htmlInfo="userAccountActionDialogObject.description"
      :actionLabel="userAccountActionDialogObject?.actionText"
      :value="userAccountActionDialogObject?.show ?? false"
      :primary="userAccountActionDialogObject.primary ?? false"
      :termination="userAccountActionDialogObject.termination ?? false"
      :destructive="userAccountActionDialogObject.destructive ?? false"
      :avatar="{
        picture: '',
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      @onAction="onActionUserConfirmationDialog"
      @input="onCloseUserConfirmationDialog"
    />
  </v-container>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig";
import { useAccountViewState } from "./composables/useAccountViewState";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import PageTitle from "@components/common/PageTitle.vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import AccountStatusBadge from "@components/AccountStatusBadge/AccountStatusBadge.vue";
import { getListofUser } from "@/api";
import { format } from "date-fns";
import OwnerShipModal from "@components/OwnerShipModal/OwnerShipModal.vue";
import {
  transferOwnerShip,
  userResendInvite,
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
} from "@/api";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import ChatterBlankView from "./ChatterBlankView.vue";
const app = getCurrentInstance();
const { proxy } = app;
const accountId = proxy.$route.params.id;

const tableConfig = useTableConfig();
const viewState = useAccountViewState();

// Check if coming from user view
const isFromUserView = ref(localStorage.getItem("isUserView") === "true");

const emptyObjectData = computed(() => ({
  singular: isFromUserView.value ? "User" : "Account",
  plural: isFromUserView.value ? "Users" : "Accounts",
}));

// Dynamic page title based on source
const pageTitle = computed(() => {
  return isFromUserView.value ? "Users" : "Accounts";
});

// Dynamic breadcrumbs based on source
const dynamicBreadcrumbs = computed(() => {
  if (isFromUserView.value) {
    return [
      {
        text: "Users",
        disabled: false,
        href: "/users",
      },
      {
        text: "User",
        disabled: true,
      },
    ];
  } else {
    return [
      {
        text: "Accounts",
        disabled: false,
        href: "/accounts",
      },
      {
        text: "Account",
        disabled: true,
      },
    ];
  }
});

// Count only users that have access or are active
const activeUsersCount = computed(() => {
  return users.value.filter((user) => {
    // Count only operational users: have access, not suspended, and not closed/blocked
    return (
      user.has_access &&
      !user.is_suspended &&
      user.user.state !== "closed" &&
      user.user.state !== "blocked"
    );
  }).length;
});

const appAccessTypes = ref([
  {
    key: "has_readiness_access",
    permissionType: "App Permission",
    appDropdown: "Proficiency",
    IsEnabled: true,
  },
  {
    key: "has_knowledge_access",
    permissionType: "App Permission",
    appDropdown: "Knowledge",
    IsEnabled: true,
  },
  {
    key: "has_performance_access",
    permissionType: "App Permission",
    appDropdown: "Performance",
    IsEnabled: true,
  },
  {
    key: "has_compensation_access",
    permissionType: "App Permission",
    appDropdown: "Compensation",
    IsEnabled: true,
  },
]);

let selectedTab = ref(0);

const users = ref([]);
const items = ref([]);
const selectedAccount = ref({});

// User actions state management
const currentSelectedRow = ref({});
const userActions = ref({
  showResendInvitePopup: false,
  showSuspendPopup: false,
  showGiveBackPopup: false,
});

const menuItems = computed(() => [
  { title: "Access to Account", count: users.value.length },
  { title: "Chatter", count: 0 },
]);

// User action dialog configuration
const userAccountActionDialogObject = computed(() => {
  if (userActions.value.showResendInvitePopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Resend Invite",
      description: `You are about to resend invite to ${firstName} ${lastName}`,
      actionText: "Resend",
      primary: true,
      show: true,
      termination: false,
    };
  } else if (userActions.value.showSuspendPopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Suspend Access",
      description: `You are about to suspend access for ${firstName} ${lastName}`,
      actionText: "Suspend",
      show: true,
      primary: false,
      termination: false,
      destructive: true,
    };
  } else if (userActions.value.showGiveBackPopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Give back Access",
      description: `You are about to give back access for ${firstName} ${lastName}`,
      actionText: "Give back Access",
      show: true,
      primary: true,
      termination: false,
      destructive: false,
    };
  }
  return {
    title: "",
    description: "",
    actionText: "",
    show: false,
    primary: false,
    termination: false,
  };
});

const handleTabChange = (tabIndex) => {
  localStorage.setItem("selectedTab", tabIndex);
  selectedTab.value = tabIndex;
};

const filteredUsers = computed(() => {
  let usersList = users?.value;

  if (
    viewState.state.filters.searchText &&
    viewState.state.filters.searchText.length > 0
  ) {
    const searchText = viewState.state.filters.searchText.toLowerCase();

    usersList = usersList.filter((el) => {
      const fullName =
        `${el.user.first_name} ${el.user.last_name}`.toLowerCase();
      return (
        fullName.includes(searchText) ||
        el.user.first_name.toLowerCase().includes(searchText) ||
        el.user.last_name.toLowerCase().includes(searchText) ||
        (el.title || "").toLowerCase().includes(searchText) // title can be null
      );
    });
  }

  // Access Status Order
  const getAccessStatus = (user) => {
    if (user.user.state === "invited") return "Invited";
    if (user.has_access) return "Has Access";
    if (user.is_suspended) return "Suspended";
    if (!user.has_access) return "Blocked";
    return "Closed";
  };

  const accessOrder = {
    Invited: 0, // Invited should be first
    "Has Access": 1, // Then Has Access
    Suspended: 2, // Followed by Suspended
    Blocked: 3, // Then Blocked
    Closed: 4, // Closed should be at the end
  };

  usersList = usersList.sort((a, b) => {
    // Sort by Access Status
    const statusA = accessOrder[getAccessStatus(a)] ?? 5;
    const statusB = accessOrder[getAccessStatus(b)] ?? 5;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    // Sort by Account Name if status is equal
    const accountNameA = a?.account_name?.toLowerCase() || "";
    const accountNameB = b?.account_name?.toLowerCase() || "";

    if (accountNameA < accountNameB) return -1;
    if (accountNameA > accountNameB) return 1;

    // Fallback to sorting by user names
    const firstNameA = a?.user?.first_name?.toLowerCase() || "";
    const firstNameB = b?.user?.first_name?.toLowerCase() || "";
    const lastNameA = a?.user?.last_name?.toLowerCase() || "";
    const lastNameB = b?.user?.last_name?.toLowerCase() || "";

    if (firstNameA < firstNameB) return -1;
    if (firstNameA > firstNameB) return 1;
    if (lastNameA < lastNameB) return -1;
    if (lastNameA > lastNameB) return 1;

    return 0;
  });

  return usersList;
});

const transferOwner = async (event) => {
  proxy.$loader.show();

  try {
    const obj = {
      email: event.user.email,
    };

    await transferOwnerShip(accountId, obj);

    // Reload users after successful transfer
    const { data } = await getListofUser(accountId);
    users.value = data.results.map((item) => {
      const accessChip = [];

      if (item.has_access) {
        if (item.is_owner) {
          accessChip.push({
            permissionType: "Owner",
            appDropdown: "Owner",
            IsEnabled: true,
          });
        } else if (item.is_co_owner) {
          accessChip.push({
            permissionType: "Co-Owner",
            appDropdown: "Co-Owner",
            IsEnabled: true,
          });
        } else {
          appAccessTypes.value.forEach((access) => {
            if (item[access.key]) {
              accessChip.push(access);
            }
          });
        }
      }

      return {
        ...item,
        accessChip,
        last_logged_in_at: item.user.last_login
          ? format(new Date(item.user.last_login), "d-MMMM-yyyy HH:mm")
          : "-",
      };
    });

    // Update items for ownership transfer modal
    populateTransferItems();
    proxy.$notifications.success("Ownership transferred successfully.");
  } catch (error) {
    console.error("Failed to transfer ownership:", error);
    proxy.$toast.error("Failed to transfer ownership. Please try again.");
  } finally {
    proxy.$loader.hide();
    viewState.state.showOnwerShipDialog.show = false;
  }
};

const populateTransferItems = () => {
  items.value = [];
  users.value.forEach((element) => {
    if (element.user.state !== "closed" && element.user.state !== "blocked") {
      if (element.is_owner === false && element.is_suspended === false) {
        items.value.push({
          id: element.user.id,
          fullName: `${element.user.first_name} ${element.user.last_name}`,
          email: element.user.email,
        });
      }
    }
  });
};

// Watch for when the ownership transfer modal is opened
watch(
  () => viewState.state.showOnwerShipDialog.show,
  (newValue) => {
    if (newValue) {
      // When modal opens, populate the transfer items
      populateTransferItems();
    }
  }
);

// Watch for user action dialog changes
watch(
  () => userAccountActionDialogObject.value,
  (newValue) => {
    console.log("User action dialog object changed:", newValue);
  },
  { deep: true }
);

// Watch for user actions changes
watch(
  () => userActions.value,
  (newValue) => {
    console.log("User actions changed:", newValue);
  },
  { deep: true }
);

onMounted(async () => {
  const stored = localStorage.getItem("selectedAccount");
  if (stored) {
    selectedAccount.value = JSON.parse(stored);
  }

  proxy.$loader.show();

  try {
    const { data } = await getListofUser(accountId);

    users.value = data.results.map((item) => {
      const accessChip = [];

      if (item.has_access) {
        if (item.is_owner) {
          accessChip.push({
            permissionType: "Owner",
            appDropdown: "Owner",
            IsEnabled: true,
          });
        } else if (item.is_co_owner) {
          accessChip.push({
            permissionType: "Co-Owner",
            appDropdown: "Co-Owner",
            IsEnabled: true,
          });
        } else {
          appAccessTypes.value.forEach((access) => {
            if (item[access.key]) {
              accessChip.push(access);
            }
          });
        }
      }

      return {
        ...item, // 👈 include full item for use in components
        accessChip,
        last_logged_in_at: item.user.last_login
          ? format(new Date(item.user.last_login), "d-MMMM-yyyy HH:mm")
          : "-",
      };
    });

    // Populate items for ownership transfer
    populateTransferItems();
  } catch (error) {
    console.error("Failed to fetch users:", error);
  } finally {
    proxy.$loader.hide();
  }
});

// User action methods
const resendInviteAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        user: currentSelectedRow.value.user.id,
        account: accountId,
      };

      await userResendInvite(currentSelectedRow.value.user.id, obj);

      // Reload users after successful action
      await reloadUsers();

      proxy.$toast.success("Invite resent successfully");
    }
  } catch (error) {
    console.error("Failed to resend invite:", error);
    proxy.$toast.error("Failed to resend invite. Please try again.");
  } finally {
    proxy.$loader.hide();
    userActions.value.showResendInvitePopup = false;
  }
};

const suspendAccessAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        email: currentSelectedRow.value.user.email,
        state: "suspended",
      };

      await userStateUpdateSuspend(accountId, currentSelectedRow.value.id, obj);

      // Reload users after successful action
      await reloadUsers();

      proxy.$toast.success("User access suspended successfully");
    }
  } catch (error) {
    console.error("Failed to suspend access:", error);
    if (error.response?.data?.detail) {
      proxy.$toast.error(error.response.data.detail);
    } else {
      proxy.$toast.error("Failed to suspend access. Please try again.");
    }
  } finally {
    proxy.$loader.hide();
    userActions.value.showSuspendPopup = false;
  }
};

const giveBackAccessAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        email: currentSelectedRow.value.user.email,
        state: "invited",
      };

      await userStateUpdateGiveBack(
        accountId,
        currentSelectedRow.value.id,
        obj
      );

      // Reload users after successful action
      await reloadUsers();

      proxy.$toast.success("User access restored successfully");
    }
  } catch (error) {
    console.error("Failed to give back access:", error);
    if (error.response?.data?.detail) {
      proxy.$toast.error(error.response.data.detail);
    } else {
      proxy.$toast.error("Failed to restore access. Please try again.");
    }
  } finally {
    proxy.$loader.hide();
    userActions.value.showGiveBackPopup = false;
  }
};

// Helper method to reload users
const reloadUsers = async () => {
  const { data } = await getListofUser(accountId);
  users.value = data.results.map((item) => {
    const accessChip = [];

    if (item.has_access) {
      if (item.is_owner) {
        accessChip.push({
          permissionType: "Owner",
          appDropdown: "Owner",
          IsEnabled: true,
        });
      } else if (item.is_co_owner) {
        accessChip.push({
          permissionType: "Co-Owner",
          appDropdown: "Co-Owner",
          IsEnabled: true,
        });
      } else {
        appAccessTypes.value.forEach((access) => {
          if (item[access.key]) {
            accessChip.push(access);
          }
        });
      }
    }

    return {
      ...item,
      accessChip,
      last_logged_in_at: item.user.last_login
        ? format(new Date(item.user.last_login), "d-MMMM-yyyy HH:mm")
        : "-",
    };
  });

  // Update items for ownership transfer modal
  populateTransferItems();
};

// User action dialog handlers
const onActionUserConfirmationDialog = () => {
  if (userActions.value.showResendInvitePopup) {
    resendInviteAction();
  } else if (userActions.value.showSuspendPopup) {
    suspendAccessAction();
  } else if (userActions.value.showGiveBackPopup) {
    giveBackAccessAction();
  }
};

const onCloseUserConfirmationDialog = () => {
  userActions.value.showResendInvitePopup = false;
  userActions.value.showSuspendPopup = false;
  userActions.value.showGiveBackPopup = false;
};

// Custom dropdown handler to intercept operations before they reach viewState
const handleDropdownOperation = (operation, item) => {
  console.log("Dropdown operation triggered:", operation);
  console.log("Item data:", item);

  // Store the current selected row for actions
  currentSelectedRow.value = filteredUsers.value.find(
    (user) => user.id === item.id
  );

  console.log("Current selected row:", currentSelectedRow.value);

  if (operation.value === "resend_invite") {
    console.log("Setting resend invite popup to true");
    userActions.value.showResendInvitePopup = true;
  } else if (operation.value === "suspend") {
    userActions.value.showSuspendPopup = true;
  } else if (operation.value === "give_back_access") {
    userActions.value.showGiveBackPopup = true;
  } else {
    // For other operations like transfer ownership, delegate to viewState
    viewState.handleAccountUserDropdownOperation(operation);
  }
};

// Override the table config to use our custom dropdown handler
const customTableConfig = computed(() => {
  const originalConfig = tableConfig.tableParams.value;

  // Find the dropdown column and override its events
  const modifiedColumns = originalConfig.columns.map((column) => {
    if (column.component === "Dropdown") {
      return {
        ...column,
        events: (item) => ({
          onClick: (operation) => {
            handleDropdownOperation(operation, item);
          },
        }),
      };
    }
    return column;
  });

  return {
    ...originalConfig,
    columns: modifiedColumns,
  };
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
}

::v-deep(.table-content) {
  border: none !important;
}

.account-header {
  margin-top: 25px;
  padding: 5px 14px;
  display: flex;
  gap: 13px;
  align-items: center;
}

.account-header-name {
  font-size: 14px;
  font-weight: bold;
}

.account-header p {
  margin-bottom: 0px !important;
}

::v-deep(.menu-tabs) {
  padding: 10px !important;
  margin-right: 10px !important;
}

::v-deep(.storybook-status-badge .v-badge__badge) {
  position: absolute !important;
  top: -7px !important;
}
.account-header-email {
  font-size: 12px;
  margin-top: 30px;
  color: gray;
}

::v-deep .custom-assign .allocation_icon > .content {
  top: 45%;
}
</style>
